import React, { useState } from 'react';
import './Homepage.css';
import Features from './Features';
import Team from './Team';
import CallToAction from './CallToAction';
import Contact from './Contact';
import Footer from './Footer';
import { sendEvent } from '../analytics';

const Homepage = () => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [contactCardOpen, setContactCardOpen] = useState(false);
  const [copySuccess, setCopySuccess] = useState('');
  
  // Debug: Check if elements exist when component mounts
  React.useEffect(() => {
    console.log('Homepage mounted, checking for elements:');
    console.log('features-section:', document.getElementById('features-section'));
    console.log('contact-section:', document.getElementById('contact-section'));
  }, []);

  // Close contact card when clicking outside
  React.useEffect(() => {
    const handleClickOutside = (event) => {
      if (contactCardOpen && !event.target.closest('.contact-card') && !event.target.closest('.contact-button')) {
        setContactCardOpen(false);
        try {
          if (typeof window !== 'undefined' && window.innerWidth > 768) {
            sendEvent('contact_card_close', { device: 'desktop' });
          }
        } catch (_) {}
      }
    };

    if (contactCardOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [contactCardOpen]);

  const toggleMobileMenu = () => {
    setMobileMenuOpen(!mobileMenuOpen);
  };

  const toggleContactCard = () => {
    const next = !contactCardOpen;
    setContactCardOpen(next);
    try {
      if (typeof window !== 'undefined' && window.innerWidth > 768) {
        sendEvent(next ? 'contact_card_open' : 'contact_card_close', { device: 'desktop' });
      }
    } catch (_) {}
  };

  const copyToClipboard = async (text, type) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopySuccess(`${type} gekopieerd!`);
      console.log(`${type} copied to clipboard: ${text}`);
      
      // Hide success message after 3 seconds
      setTimeout(() => {
        setCopySuccess('');
      }, 3000);
    } catch (err) {
      console.error('Failed to copy to clipboard:', err);
      // Fallback for older browsers
      const textArea = document.createElement('textarea');
      textArea.value = text;
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand('copy');
      document.body.removeChild(textArea);
      
      setCopySuccess(`${type} gekopieerd!`);
      setTimeout(() => {
        setCopySuccess('');
      }, 3000);
    }
  };

  const handleDesktopContactClick = () => {
    // Check if it's desktop (screen width > 768px)
    if (window.innerWidth > 768) {
      toggleContactCard();
    } else {
      // Mobile behavior - scroll to contact section
      scrollToSection('contact-section');
    }
  };

  const scrollToSection = (sectionId) => {
    console.log('scrollToSection called with:', sectionId);
    const element = document.getElementById(sectionId);
    console.log('Found element:', element);
    if (element) {
      element.scrollIntoView({ 
        behavior: 'smooth',
        block: 'start'
      });
      console.log('Scrolling to element:', element);
    } else {
      console.log('Element not found with ID:', sectionId);
    }
    // Close mobile menu if open
    if (mobileMenuOpen) {
      setMobileMenuOpen(false);
    }
  };

  const ScrollArrow = ({ targetSection, className }) => (
    <div 
      className={`scroll-arrow ${className || ''}`}
      onClick={() => {
        try {
          const sectionKey = (targetSection || '').replace('-section', '');
          sendEvent('scroll_arrow_click', { target_section: sectionKey || targetSection });
        } catch (_) {}
        scrollToSection(targetSection);
      }}
    >
      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path 
          d="M7 10L12 15L17 10" 
          stroke="currentColor" 
          strokeWidth="2" 
          strokeLinecap="round" 
          strokeLinejoin="round"
        />
      </svg>
    </div>
  );

  return (
    <>
    {/* Copy Success Toast */}
    {copySuccess && (
      <div className="copy-toast">
        <svg className="toast-icon" width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
          <circle cx="10" cy="10" r="8" fill="rgba(255, 255, 255, 0.3)"/>
          <path d="M6 10l3 3 6-6" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
        </svg>
        <span>{copySuccess}</span>
      </div>
    )}
    
    <div className="homepage-container">
      <div className="background-section">
        <img 
          src="https://api.builder.io/api/v1/image/assets/TEMP/f43df5e7fe7712f3b37653899e94be7a109655f6?width=2880" 
          className="background-image"
          alt="Background" 
        />
        <div className="horizontal-line-top"></div>
        <div className="vertical-line"></div>
        <ScrollArrow targetSection="features-section" className="background-arrow" />
      </div>

      <div className="main-content">
        <div className="hero-heading">
          Expertise in ruimtelijke ontwikkeling
        </div>
        
        <div className="content-bottom">
          <div className="subtitle-text">
            Van concept tot realisatie, wij denken mee in uw plannen
          </div>
          
          <div className="right-section">
            <div className="description-text">
            Professioneel advies over planologie, gebiedseconomie en toekomstgerichte gebiedsontwikkeling. Werkers voor duurzame ruimtelijke projecten.
            </div>
            
            <div className="button-group">
              <div className="primary-button" onClick={() => {
                console.log('Primary button clicked, scrolling to features-section');
                try { sendEvent('click_cta', { cta_id: 'hero_primary_services', location: 'hero' }); } catch (_) {}
                scrollToSection('features-section');
              }}>
                <span className="button-text-primary">Onze diensten</span>
                <svg className="arrow-icon" width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M7 17L17 7" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  <path d="M7 7H17V17" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </div>
              
              <div className="secondary-button" onClick={() => {
                console.log('Secondary button clicked, scrolling to contact-section');
                try { sendEvent('click_cta', { cta_id: 'hero_secondary_contact', location: 'hero' }); } catch (_) {}
                scrollToSection('contact-section');
              }}>
                <span className="button-text-secondary">Neem Contact Op</span>
                <svg className="box-icon" width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M21 16V8.00002C20.9996 7.6493 20.9071 7.30483 20.7315 7.00119C20.556 6.69754 20.3037 6.44539 20 6.27002L13 2.27002C12.696 2.09449 12.3511 2.00208 12 2.00208C11.6489 2.00208 11.304 2.09449 11 2.27002L4 6.27002C3.69626 6.44539 3.44398 6.69754 3.26846 7.00119C3.09294 7.30483 3.00036 7.6493 3 8.00002V16C3.00036 16.3508 3.09294 16.6952 3.26846 16.9989C3.44398 17.3025 3.69626 17.5547 4 17.73L11 21.73C11.304 21.9056 11.6489 21.998 12 21.998C12.3511 21.998 12.696 21.9056 13 21.73L20 17.73C20.3037 17.5547 20.556 17.3025 20.7315 16.9989C20.9071 16.6952 20.9996 16.3508 21 16Z" stroke="url(#paint0_linear_2_91)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  <path d="M3.26953 6.95996L11.9995 12.01L20.7295 6.95996" stroke="url(#paint1_linear_2_91)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  <path d="M12 22.08V12" stroke="url(#paint2_linear_2_91)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  <defs>
                    <linearGradient id="paint0_linear_2_91" x1="6.51" y1="3.90169" x2="19.2169" y2="19.2146" gradientUnits="userSpaceOnUse">
                      <stop stopColor="#FF7557"/>
                      <stop offset="0.31087" stopColor="#DE35FF"/>
                      <stop offset="0.704599" stopColor="#173EFF"/>
                      <stop offset="1" stopColor="#16F1FF"/>
                    </linearGradient>
                    <linearGradient id="paint1_linear_2_91" x1="6.67423" y1="7.43971" x2="8.02221" y2="13.6788" gradientUnits="userSpaceOnUse">
                      <stop stopColor="#FF7557"/>
                      <stop offset="0.31087" stopColor="#DE35FF"/>
                      <stop offset="0.704599" stopColor="#173EFF"/>
                      <stop offset="1" stopColor="#16F1FF"/>
                    </linearGradient>
                    <linearGradient id="paint2_linear_2_91" x1="12.195" y1="12.9576" x2="13.8961" y2="13.1835" gradientUnits="userSpaceOnUse">
                      <stop stopColor="#FF7557"/>
                      <stop offset="0.31087" stopColor="#DE35FF"/>
                      <stop offset="0.704599" stopColor="#173EFF"/>
                      <stop offset="1" stopColor="#16F1FF"/>
                    </linearGradient>
                  </defs>
                </svg>
              </div>
            </div>
          </div>
        </div>
      </div>

      <nav className="navigation-bar">
        <div className="nav-left">
          <div className="logo">
            <svg width="30" height="30" viewBox="0 0 30 30" fill="none" xmlns="http://www.w3.org/2000/svg">
              <circle cx="15" cy="15" r="13" stroke="url(#paint0_linear_logo)" strokeWidth="4"/>
              <defs>
                <linearGradient id="paint0_linear_logo" x1="5.85" y1="2.85" x2="24.45" y2="27.75" gradientUnits="userSpaceOnUse">
                  <stop stopColor="#FF7557"/>
                  <stop offset="0.5" stopColor="#173EFF"/>
                  <stop offset="1" stopColor="#16F1FF"/>
                </linearGradient>
              </defs>
            </svg>
            <span className="logo-text">Waardewerkers Advies</span>
          </div>

          <div className="menu-section">
            <div className="home-menu-item">
              <span className="menu-text">Home</span>
            </div>
            <span className="menu-text" onClick={() => { try { sendEvent('click_nav', { item_id: 'services', menu: 'top' }); } catch (_) {} scrollToSection('features-section'); }} style={{cursor: 'pointer'}}>Services</span>
            <span className="menu-text" onClick={() => { try { sendEvent('click_nav', { item_id: 'gebiedseconomie', menu: 'top' }); } catch (_) {} scrollToSection('cta-section'); }} style={{cursor: 'pointer'}}>Gebiedseconomie</span>
            <span className="menu-text" onClick={() => { try { sendEvent('click_nav', { item_id: 'over_ons', menu: 'top' }); } catch (_) {} scrollToSection('team-section'); }} style={{cursor: 'pointer'}}>Over ons</span>
          </div>
        </div>

        <div className="nav-right">
          <div className="contact-button" onClick={handleDesktopContactClick} style={{cursor: 'pointer'}}>
            <svg className="phone-icon" width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
              <g clipPath="url(#clip0_2_110)">
                <path d="M14.6669 11.2797V13.2797C14.6677 13.4654 14.6297 13.6492 14.5553 13.8193C14.4809 13.9894 14.3718 14.1421 14.235 14.2676C14.0982 14.3932 13.9367 14.4887 13.7608 14.5482C13.5849 14.6077 13.3985 14.6298 13.2136 14.6131C11.1622 14.3902 9.19161 13.6892 7.46028 12.5664C5.8495 11.5428 4.48384 10.1772 3.46028 8.56641C2.3336 6.82721 1.63244 4.84707 1.41361 2.78641C1.39695 2.60205 1.41886 2.41625 1.47795 2.24082C1.53703 2.0654 1.63199 1.9042 1.75679 1.76749C1.88159 1.63077 2.03348 1.52155 2.20281 1.44675C2.37213 1.37196 2.55517 1.33325 2.74028 1.33307H4.74028C5.06382 1.32989 5.37748 1.44446 5.62279 1.65543C5.8681 1.8664 6.02833 2.15937 6.07361 2.47974C6.15803 3.11978 6.31458 3.74822 6.54028 4.35307C6.62998 4.59169 6.64939 4.85102 6.59622 5.10033C6.54305 5.34964 6.41952 5.57848 6.24028 5.75974L5.39361 6.60641C6.34265 8.27544 7.72458 9.65737 9.39361 10.6064L10.2403 9.75974C10.4215 9.5805 10.6504 9.45697 10.8997 9.4038C11.149 9.35063 11.4083 9.37004 11.6469 9.45974C12.2518 9.68544 12.8802 9.84199 13.5203 9.92641C13.8441 9.97209 14.1399 10.1352 14.3513 10.3847C14.5627 10.6343 14.6751 10.9528 14.6669 11.2797Z" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
              </g>
              <defs>
                <clipPath id="clip0_2_110">
                  <rect width="16" height="16" fill="white"/>
                </clipPath>
              </defs>
            </svg>
            <span className="contact-text">Contact</span>
          </div>
          
          <div className={`hamburger-menu ${mobileMenuOpen ? 'active' : ''}`} onClick={toggleMobileMenu}>
            <div className="hamburger-line"></div>
            <div className="hamburger-line"></div>
            <div className="hamburger-line"></div>
          </div>
        </div>

        <div className={`mobile-menu ${mobileMenuOpen ? 'active' : ''}`}>
          <div className="mobile-menu-item">
            <span className="menu-text">Home</span>
          </div>
          <div className="mobile-menu-item" onClick={() => { try { sendEvent('click_nav', { item_id: 'services', menu: 'mobile' }); } catch (_) {} scrollToSection('features-section'); }} style={{cursor: 'pointer'}}>
            <span className="menu-text">Services</span>
          </div>
          <div className="mobile-menu-item" onClick={() => { try { sendEvent('click_nav', { item_id: 'gebiedseconomie', menu: 'mobile' }); } catch (_) {} scrollToSection('cta-section'); }} style={{cursor: 'pointer'}}>
            <span className="menu-text">Gebiedseconomie</span>
          </div>
          <div className="mobile-menu-item" onClick={() => { try { sendEvent('click_nav', { item_id: 'over_ons', menu: 'mobile' }); } catch (_) {} scrollToSection('team-section'); }} style={{cursor: 'pointer'}}>
            <span className="menu-text">Over ons</span>
          </div>
          <div className="mobile-menu-item" onClick={() => { try { sendEvent('click_nav', { item_id: 'contact', menu: 'mobile' }); } catch (_) {} scrollToSection('contact-section'); }} style={{cursor: 'pointer'}}>
            <div className="contact-button">
              <svg className="phone-icon" width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                <g clipPath="url(#clip0_2_110_mobile)">
                  <path d="M14.6669 11.2797V13.2797C14.6677 13.4654 14.6297 13.6492 14.5553 13.8193C14.4809 13.9894 14.3718 14.1421 14.235 14.2676C14.0982 14.3932 13.9367 14.4887 13.7608 14.5482C13.5849 14.6077 13.3985 14.6298 13.2136 14.6131C11.1622 14.3902 9.19161 13.6892 7.46028 12.5664C5.8495 11.5428 4.48384 10.1772 3.46028 8.56641C2.3336 6.82721 1.63244 4.84707 1.41361 2.78641C1.39695 2.60205 1.41886 2.41625 1.47795 2.24082C1.53703 2.0654 1.63199 1.9042 1.75679 1.76749C1.88159 1.63077 2.03348 1.52155 2.20281 1.44675C2.37213 1.37196 2.55517 1.33325 2.74028 1.33307H4.74028C5.06382 1.32989 5.37748 1.44446 5.62279 1.65543C5.8681 1.8664 6.02833 2.15937 6.07361 2.47974C6.15803 3.11978 6.31458 3.74822 6.54028 4.35307C6.62998 4.59169 6.64939 4.85102 6.59622 5.10033C6.54305 5.34964 6.41952 5.57848 6.24028 5.75974L5.39361 6.60641C6.34265 8.27544 7.72458 9.65737 9.39361 10.6064L10.2403 9.75974C10.4215 9.5805 10.6504 9.45697 10.8997 9.4038C11.149 9.35063 11.4083 9.37004 11.6469 9.45974C12.2518 9.68544 12.8802 9.84199 13.5203 9.92641C13.8441 9.97209 14.1399 10.1352 14.3513 10.3847C14.5627 10.6343 14.6751 10.9528 14.6669 11.2797Z" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                </g>
                <defs>
                  <clipPath id="clip0_2_110_mobile">
                    <rect width="16" height="16" fill="white"/>
                  </clipPath>
                </defs>
              </svg>
              <span className="contact-text">Contact</span>
            </div>
          </div>
        </div>

        {/* Contact Card */}
        <div className={`contact-card ${contactCardOpen ? 'active' : ''}`}>
          <div className="contact-card-header">
            <h3>Contact</h3>
            <button className="close-button" onClick={toggleContactCard}>
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M18 6L6 18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                <path d="M6 6L18 18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </button>
          </div>
          <div className="contact-card-content">
            {/* <div className="contact-item" onClick={() => copyToClipboard('033 - 123 45 67', 'Telefoonnummer')}> */}
              {/* <svg className="contact-icon" width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M18.3336 14.0996V16.0996C18.3346 16.3168 18.2871 16.5315 18.1941 16.7241C18.1011 16.9168 17.9647 17.0826 17.7937 17.2095C17.6228 17.3364 17.4209 17.4209 17.201 17.4602C16.9811 17.4996 16.7481 17.4873 16.5336 17.4164C14.2027 16.9878 11.9895 15.8615 10.0753 14.208C8.31187 12.6785 6.85479 10.7715 5.80029 8.62801C4.16699 6.69035 3.04305 4.45265 2.62529 2.09801C2.55435 1.88532 2.54151 1.65427 2.58045 1.43632C2.61939 1.21837 2.70248 1.01804 2.82789 0.847552C2.9533 0.677063 3.11686 0.540689 3.30709 0.447252C3.49732 0.353815 3.70977 0.305664 3.92529 0.306012H5.92529C6.32991 0.301508 6.71847 0.455579 7.02848 0.736787C7.33849 1.01799 7.54791 1.40742 7.61696 1.84134C7.74754 2.70975 7.97322 3.56028 8.29196 4.37801C8.40247 4.65211 8.43674 4.95627 8.39122 5.25041C8.34569 5.54456 8.22194 5.81811 8.03362 6.03801L7.00696 7.06468C8.2847 9.09405 10.0559 10.8652 12.0853 12.143L13.1119 11.1163C13.3318 10.928 13.6054 10.8043 13.8995 10.7587C14.1937 10.7132 14.4978 10.7475 14.7719 10.858C15.5897 11.1768 16.4402 11.4024 17.3086 11.533C17.7462 11.6027 18.1381 11.8156 18.4197 12.1294C18.7013 12.4431 18.8526 12.8359 18.8419 13.2413L18.3336 14.0996Z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
              </svg> */}
              {/* <div className="contact-details">
                <span className="contact-label">Telefoon</span>
                <span className="contact-value">06</span>
              </div> */}
              {/* <svg className="copy-icon" width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M13.3333 6H7.33333C6.59695 6 6 6.59695 6 7.33333V13.3333C6 14.0697 6.59695 14.6667 7.33333 14.6667H13.3333C14.0697 14.6667 14.6667 14.0697 14.6667 13.3333V7.33333C14.6667 6.59695 14.0697 6 13.3333 6Z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                <path d="M3.33333 10H2.66667C2.31305 10 1.97391 9.85952 1.72386 9.60948C1.47381 9.35943 1.33333 9.02029 1.33333 8.66667V2.66667C1.33333 2.31305 1.47381 1.97391 1.72386 1.72386C1.97391 1.47381 2.31305 1.33333 2.66667 1.33333H8.66667C9.02029 1.33333 9.35943 1.47381 9.60948 1.72386C9.85952 1.97391 10 2.31305 10 2.66667V3.33333" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </div> */}
            <div className="contact-item" onClick={() => copyToClipboard('info@waardewerkers.nl', 'E-mail')}>
              <svg className="contact-icon" width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M3.33333 3.33334H16.6667C17.5833 3.33334 18.3333 4.08334 18.3333 5.00001V15C18.3333 15.9167 17.5833 16.6667 16.6667 16.6667H3.33333C2.41667 16.6667 1.66667 15.9167 1.66667 15V5.00001C1.66667 4.08334 2.41667 3.33334 3.33333 3.33334Z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                <path d="M18.3333 5L10 10.8333L1.66667 5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
              <div className="contact-details">
                <span className="contact-label">E-mail</span>
                <span className="contact-value">info@waardewerkers.nl</span>
              </div>
              <svg className="copy-icon" width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M13.3333 6H7.33333C6.59695 6 6 6.59695 6 7.33333V13.3333C6 14.0697 6.59695 14.6667 7.33333 14.6667H13.3333C14.0697 14.6667 14.6667 14.0697 14.6667 13.3333V7.33333C14.6667 6.59695 14.0697 6 13.3333 6Z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                <path d="M3.33333 10H2.66667C2.31305 10 1.97391 9.85952 1.72386 9.60948C1.47381 9.35943 1.33333 9.02029 1.33333 8.66667V2.66667C1.33333 2.31305 1.47381 1.97391 1.72386 1.72386C1.97391 1.47381 2.31305 1.33333 2.66667 1.33333H8.66667C9.02029 1.33333 9.35943 1.47381 9.60948 1.72386C9.85952 1.97391 10 2.31305 10 2.66667V3.33333" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </div>
            <div className="contact-item" onClick={() => copyToClipboard('Euclideslaan 60, 3584 BN Utrecht, Nederland', 'Adres')}>
              <svg className="contact-icon" width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M17.5 8.33333C17.5 14.1667 10 19.1667 10 19.1667C10 19.1667 2.5 14.1667 2.5 8.33333C2.5 6.34413 3.29018 4.43654 4.6967 3.03007C6.10322 1.62361 8.01088 0.833334 10 0.833334C11.9891 0.833334 13.8968 1.62361 15.3033 3.03007C16.7098 4.43654 17.5 6.34413 17.5 8.33333Z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                <path d="M10 10.8333C11.3807 10.8333 12.5 9.71405 12.5 8.33333C12.5 6.95262 11.3807 5.83333 10 5.83333C8.61929 5.83333 7.5 6.95262 7.5 8.33333C7.5 9.71405 8.61929 10.8333 10 10.8333Z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
              <div className="contact-details">
                <span className="contact-label">Adres</span>
                <span className="contact-value">Euclideslaan 60<br/>3584 BN Utrecht</span>
              </div>
              <svg className="copy-icon" width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M13.3333 6H7.33333C6.59695 6 6 6.59695 6 7.33333V13.3333C6 14.0697 6.59695 14.6667 7.33333 14.6667H13.3333C14.0697 14.6667 14.6667 14.0697 14.6667 13.3333V7.33333C14.6667 6.59695 14.0697 6 13.3333 6Z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                <path d="M3.33333 10H2.66667C2.31305 10 1.97391 9.85952 1.72386 9.60948C1.47381 9.35943 1.33333 9.02029 1.33333 8.66667V2.66667C1.33333 2.31305 1.47381 1.97391 1.72386 1.72386C1.97391 1.47381 2.31305 1.33333 2.66667 1.33333H8.66667C9.02029 1.33333 9.35943 1.47381 9.60948 1.72386C9.85952 1.97391 10 2.31305 10 2.66667V3.33333" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </div>
          </div>
        </div>
      </nav>

      <svg className="decorative-curve" width="1411" height="646" viewBox="0 0 1411 646" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M1471.49 1C1481.7 110.205 1439.91 221.91 1351.4 321.988C1262.89 422.067 1131.64 506.023 974.239 563.242C816.84 620.461 640.364 648.371 467.129 643.444C293.894 638.517 131.68 600.973 1 535.561" stroke="url(#paint0_linear_2_113)" strokeWidth="4"/>
        <defs>
          <linearGradient id="paint0_linear_2_113" x1="214.5" y1="662.5" x2="1074.8" y2="1054.45" gradientUnits="userSpaceOnUse">
            <stop stopColor="white"/>
            <stop offset="0.305824" stopColor="#FF7557"/>
            <stop offset="0.539613" stopColor="#DE35FF"/>
            <stop offset="0.704599" stopColor="#173EFF"/>
            <stop offset="1" stopColor="#16F1FF"/>
          </linearGradient>
        </defs>
      </svg>
    </div>

    <Features />
    <Team />
    <CallToAction />
    <Contact />
    <Footer />
    </>
  );
};

export default Homepage;
